import React, { useState, useMemo } from "react";
import { AlertTriangle, MoveUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useConferenceStatus } from "../services/conferenceService";

// Stagger animation for cards
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,

    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

// Utility function to truncate text
const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

// ConferencesContent component
const ConferencesContent = () => {
  const { t } = useTranslation();
  const { liveConferences, upcomingConferences } = useConferenceStatus();
  const [displayedCount, setDisplayedCount] = useState(12);

  const allConferences = useMemo(() => {
    return [...liveConferences, ...upcomingConferences]
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  }, [liveConferences, upcomingConferences]);

  const handleShowMore = () => {
    setDisplayedCount((prev) => Math.min(prev + 4, allConferences.length));
  };

  if (allConferences.length === 0) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-gray-800">
          {t("conferences.title", "Conferences")}
        </h2>
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-md max-w-2xl mx-auto">
          <div className="flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
            <p className="text-yellow-700 text-sm sm:text-base">
              {t("conferences.noResults", "No conferences found.")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden h-full mx-auto px-4 py-4 sm:px-6 sm:py-6">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 text-center text-gray-800">
        {t("conferences.title", "Conferences")}
      </h2>
      <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto text-sm sm:text-base lg:text-lg">
        {t("conferences.subtitle")}
      </p>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
      >
        <AnimatePresence>
          {allConferences.slice(0, displayedCount).map((conference) => (
            <motion.div
              key={conference.id}
              variants={item}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="relative">
                <img
                  src={conference.imageUrl}
                  alt={conference.name}
                  className={`w-full aspect-video object-cover`}
                  style={conference.name.includes("Getty Music Worship Conference") ? { objectPosition: '0 0px' } : {}}
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => window.open(conference.url, "_blank")}
                >
                  <div className="bg-stone-900 text-white rounded-full p-3 sm:p-4">
                    <MoveUpRight />
                  </div>
                </div>
                {new Date() >= new Date(conference.startDate) &&
                  new Date() <= new Date(conference.endDate) && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-1">
                      <span className="animate-pulse h-1.5 w-1.5 bg-white rounded-full" />
                      {t("conferences.live", "LIVE")}
                    </div>
                  )}
              </div>
              <div className="p-4">
                <h3
                  className="font-semibold text-gray-800 line-clamp-2 text-sm sm:text-base h-12 sm:h-14 min-h-12"
                  title={t(`conferences.${conference.id}.name`, conference.name)}
                >
                  {truncateText(t(`conferences.${conference.id}.name`, conference.name), 60)}
                </h3>
                <div className="flex items-center text-xs sm:text-sm text-gray-600 mb-2">
                  <svg
                    className="w-3 h-3 sm:w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="truncate">{t(`conferences.${conference.id}.location`, conference.location)}</span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <svg
                    className="w-3 h-3 sm:w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>{t(`conferences.${conference.id}.dates`, conference.dates)}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {displayedCount < allConferences.length && (
        <div className="text-center mt-8">
          <motion.button
            onClick={handleShowMore}
            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md shadow-sm text-white bg-stone-900 hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-700"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            {t("conferences.showMore", "Show More")}
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default ConferencesContent;  